import { useMemo, useCallback, useEffect, useRef } from 'react';
import { auth } from "@/lib";
import { useAuthStore } from '../stores/auth-store';
import type { UseAuthReturn, RoleName, Resource, Action } from '../types/rbac';

/**
 * Optimized authentication hook with caching and performance enhancements
 */
export function useAuth(): UseAuthReturn {
    // Use Zustand store for optimized state management
    const store = useAuthStore();
    const lastDataRef = useRef<any>(null);
    const lastIsPendingRef = useRef<boolean | null>(null);

    // Subscribe to Better Auth session with caching
    const { data: sessionData, isPending } = auth.useSession();

    // Update store only when necessary - using useEffect with proper dependencies
    useEffect(() => {
        // Check if data actually changed to prevent infinite loops
        const currentDataString = JSON.stringify(sessionData);
        const lastDataString = JSON.stringify(lastDataRef.current);
        const isPendingChanged = isPending !== lastIsPendingRef.current;
        
        if (currentDataString === lastDataString && !isPendingChanged) {
            return; // No change, skip update
        }
        
        // Additional safety check: prevent updates if store already has the same data
        if (!isPending && sessionData?.user && 
            store.user?.id === sessionData.user.id && 
            store.isLoading === false) {
            lastDataRef.current = sessionData;
            lastIsPendingRef.current = isPending;
            return; // Store already has the current data
        }
        
        // Update refs
        lastDataRef.current = sessionData;
        lastIsPendingRef.current = isPending;
        
        // Check if we can use cached session data
        const cachedSession = store.getSessionFromCache();
        if (cachedSession && !store.shouldRefreshSession() && !isPending) {
            store.setFromSession(cachedSession, isPending);
        } else {
            store.setFromSession(sessionData, isPending);
            // Cache the new session data
            if (sessionData && !isPending) {
                store.setSessionCache(sessionData);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionData, isPending]); // Intentionally excluded store dependencies to prevent infinite loops

    // Memoized permission helpers for better performance
    const checkPermission = useCallback((resource: Resource, action: Action) => {
        return store.hasPermission(resource, action);
    }, [store]);

    const checkRole = useCallback((role: RoleName) => {
        return store.hasRole(role);
    }, [store]);

    const checkAnyRole = useCallback((rolesToCheck: RoleName[]) => {
        return store.hasAnyRole(rolesToCheck);
    }, [store]);

    const checkAllRoles = useCallback((rolesToCheck: RoleName[]) => {
        return store.hasAllRoles(rolesToCheck);
    }, [store]);

    const signOut = useCallback(async () => {
        try {
            store.clear();
            await auth.signOut();
        } catch (e) {
            console.error('Error signing out:', e);
        }
    }, [store]);

    const refreshSession = useCallback(async () => {
        try {
            // Force refresh by clearing cache
            store.setSessionCache(null);
            const newSession = await auth.getSession();
            if (newSession) {
                store.setFromSession(newSession, false);
                store.setSessionCache(newSession);
            }
        } catch (e) {
            console.error('Error refreshing session:', e);
        }
    }, [store]);

    // Optimized session data with fallback - memoized to prevent unnecessary re-renders
    const optimizedSessionData = useMemo(() => {
        return store.sessionCache?.data || sessionData;
    }, [store.sessionCache?.data, sessionData]);

    return {
        sessionData: optimizedSessionData,
        user: store.user,
        session: store.session,
        roles: store.roles,
        permissions: store.permissions,
        isLoading: store.isLoading,
        isAuthenticated: store.isAuthenticated,
        hasPermission: checkPermission,
        hasRole: checkRole,
        hasAnyRole: checkAnyRole,
        hasAllRoles: checkAllRoles,
        signOut,
        refreshSession,
    };
}